import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import PageLayout from '../components/layout/PageLayout';
import PlayingCard from '../components/ui/PlayingCard';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../services/api';

const FONT = "'Barlow', sans-serif";
const COLORS = {
  bg: '#0d0d0d',
  card: '#111',
  cardAlt: '#1a1a1a',
  border: 'rgba(255,255,255,0.06)',
  white: '#fff',
  muted: 'rgba(255,255,255,0.35)',
  green: '#00E633',
  blue: '#009BFF',
  lime: '#C8FF00',
  red: '#e53935',
};

type UploadState = 'idle' | 'uploading' | 'success' | 'error';
type HandFilter = 'all' | 'positive' | 'negative';
type SortMode = 'default' | 'position';

type SessionId = string | number;

interface StackPoint {
  mao: number;
  stack: number;
}

interface LeakItem {
  name: string;
  category: string;
  cost_bb: number;
  severity: number;
}

interface HandItem {
  id: string;
  value: string;
  suit: string;
  value2: string;
  suit2: string;
  position: string;
  board: string;
  ev: number;
  quality: 'good' | 'review';
  analysis?: string;
}

interface DrillItem {
  name: string;
  reason: string;
  href: string;
}

interface TrainingPackState {
  trainingPack: {
    generatedAt: string;
    focusDrill: string;
    session: {
      id: SessionId;
      filename: string;
      created_at: string;
      hands_count: number;
      winrate: number;
      ev_total: number;
      roi: number;
      score: number;
    };
    leaks: LeakItem[];
    drills: DrillItem[];
  };
}

interface SessionView {
  id: SessionId;
  created_at: string;
  filename: string;
  hands_count: number;
  winrate: number;
  ev_total: number;
  roi: number;
  score: number;
  stack_data: StackPoint[];
  leaks: LeakItem[];
  hands: HandItem[];
  drills: DrillItem[];
  coachSummary?: string;
  isMock?: boolean;
}

interface StatsOverview {
  total_ev_bb?: number;
  sessions_count?: number;
  avg_hands?: number;
  win_rate?: number;
}

interface LeakTrendItem {
  name: string;
  category: string;
  currentCost: number;
  previousCost?: number;
  currentSeverity: number;
  previousSeverity?: number;
  trend: 'improved' | 'worse' | 'new' | 'stable';
  message: string;
}

interface LeakWindowInsight {
  name: string;
  category: string;
  appearances: number;
  sessionsConsidered: number;
  averageCost: number;
  trend: 'improved' | 'worse' | 'stable' | 'new';
  message: string;
  series: Array<{ slot: string; cost: number }>;
}

interface SessionsLocationState {
  focusLeakId?: string;
  source?: string;
}

const MOCK_SESSION: SessionView = {
  id: 'mock-1',
  created_at: new Date().toISOString(),
  filename: 'sessao_ggpoker_2026.txt',
  hands_count: 47,
  winrate: 8.2,
  ev_total: 2.3,
  roi: 8.2,
  score: 73,
  stack_data: [
    { mao: 1, stack: 100 },
    { mao: 10, stack: 98 },
    { mao: 20, stack: 112 },
    { mao: 30, stack: 108 },
    { mao: 40, stack: 125 },
    { mao: 47, stack: 131 },
  ],
  leaks: [
    {
      name: 'Fold excessivo no BTN',
      category: 'Posicao',
      cost_bb: -1.8,
      severity: 0.7,
    },
    {
      name: 'C-bet frequencia baixa no flop',
      category: 'Agressividade',
      cost_bb: -1.2,
      severity: 0.5,
    },
    {
      name: 'Overlimp pre-flop no SB',
      category: 'Pre-flop',
      cost_bb: -0.8,
      severity: 0.3,
    },
  ],
  hands: [
    {
      id: 'h1',
      value: 'A',
      suit: 's',
      value2: 'K',
      suit2: 'h',
      position: 'BTN vs BB',
      board: 'Kd 7c 2h · Turn: Js',
      ev: 156,
      quality: 'good',
      analysis:
        'Bom spot para pressionar o range de defesa do BB. A linha agressiva manteve a iniciativa e extraiu valor do turn.',
    },
    {
      id: 'h2',
      value: 'Q',
      suit: 'd',
      value2: 'J',
      suit2: 's',
      position: 'CO vs BTN',
      board: 'Ts 9d 3h · River: 2h',
      ev: -84,
      quality: 'review',
      analysis:
        'A mao pede revisao: o range adversario conecta forte nesse runout e a frequencia de second barrel ficou acima do ideal.',
    },
    {
      id: 'h3',
      value: 'A',
      suit: 'c',
      value2: 'Q',
      suit2: 'c',
      position: 'UTG vs BB',
      board: 'Ah Kd 5c · Turn: 8s',
      ev: 89,
      quality: 'good',
      analysis:
        'Linha solida de value bet. O sizing protegeu sua vantagem de range e capturou calls piores com alta frequencia.',
    },
  ],
  drills: [
    {
      name: 'Spots de fold no BTN',
      reason: 'Voce folda 78% aqui - meta: 55%',
      href: '/treinos',
    },
    {
      name: 'C-bet sizing no flop seco',
      reason: 'Apostas abaixo do ideal em 63% dos spots',
      href: '/treinos',
    },
    {
      name: 'Pre-flop: limp vs raise no SB',
      reason: 'Limpa 44% quando deveria subir',
      href: '/treinos',
    },
  ],
  coachSummary:
    'Sessao demo com foco em ajustes de agressao controlada e defesa de blinds.',
  isMock: true,
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatDate(date: string) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'Sem data';
  return parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatSigned(value: number, suffix = '') {
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toFixed(1)}${suffix}`;
}

function formatMoneyEv(value: number) {
  const prefix = value > 0 ? '+' : '';
  return `${prefix}R$${Math.abs(value).toFixed(0)}`;
}

function parseBoardCards(board: string) {
  const matches = board.match(/[2-9TJQKA][shdc]/gi) || [];
  return matches.slice(0, 5);
}

function buildStackData(handsCount: number, evTotal: number) {
  const points = 6;
  const safeHands = Math.max(handsCount, points);
  return Array.from({ length: points }).map((_, index) => {
    const progress = index / (points - 1);
    const mao = Math.max(1, Math.round(progress * safeHands));
    const variance = Math.sin(index * 1.3) * 5;
    return {
      mao,
      stack: Number((100 + evTotal * 8 * progress + variance).toFixed(1)),
    };
  });
}

function fallbackLeaks(session: {
  winrate: number;
  ev_total: number;
  hands_count: number;
  filename: string;
}) {
  const severityBase = clamp(Math.abs(session.ev_total) / 4, 0.25, 0.85);
  return [
    {
      name: session.winrate < 0 ? 'Defesa passiva de blinds' : 'Fold excessivo no BTN',
      category: 'Posicao',
      cost_bb: Number((-1.6 * severityBase).toFixed(1)),
      severity: clamp(severityBase, 0.3, 0.8),
    },
    {
      name: 'Frequencia de c-bet inconsistente',
      category: 'Agressividade',
      cost_bb: Number((-1.1 * severityBase).toFixed(1)),
      severity: clamp(severityBase * 0.8, 0.25, 0.7),
    },
    {
      name:
        session.hands_count > 80
          ? 'Selecao de spots pre-flop imprecisa'
          : `Ajustes iniciais em ${session.filename.includes('gg') ? 'GGPoker' : 'sessao'}`,
      category: 'Pre-flop',
      cost_bb: Number((-0.8 * severityBase).toFixed(1)),
      severity: clamp(severityBase * 0.6, 0.2, 0.55),
    },
  ];
}

function fallbackHands(session: {
  id: SessionId;
  ev_total: number;
  filename: string;
}): HandItem[] {
  const bias = session.ev_total >= 0 ? 1 : -1;
  return [
    {
      id: `${session.id}-hand-1`,
      value: 'A',
      suit: 's',
      value2: 'Q',
      suit2: 'd',
      position: 'BTN vs BB',
      board: 'Kd 7c 2h · Turn: Js',
      ev: bias > 0 ? 128 : -42,
      quality: bias > 0 ? 'good' : 'review',
      analysis:
        'Analise sintetica gerada a partir da sessao. Vale revisar o planejamento da mao desde o pre-flop ate o turn.',
    },
    {
      id: `${session.id}-hand-2`,
      value: 'J',
      suit: 'h',
      value2: 'T',
      suit2: 'h',
      position: 'CO vs BTN',
      board: 'Ts 8h 4c · River: 2d',
      ev: bias > 0 ? 74 : -96,
      quality: bias > 0 ? 'good' : 'review',
      analysis:
        'Spot relevante para estudar frequencias de barrel e selecao de sizings em runouts desconfortaveis.',
    },
    {
      id: `${session.id}-hand-3`,
      value: '9',
      suit: 'c',
      value2: '9',
      suit2: 's',
      position: 'SB vs BB',
      board: '9d 5s 2c · Turn: Kh',
      ev: bias > 0 ? -18 : 56,
      quality: bias > 0 ? 'review' : 'good',
      analysis:
        'Mao util para revisar planejamento por street e identificar onde o range do vilao fica capado.',
    },
  ];
}

function fallbackDrills(leaks: LeakItem[]): DrillItem[] {
  return leaks.slice(0, 3).map((leak) => ({
    name:
      leak.category === 'Posicao'
        ? 'Spots de fold no BTN'
        : leak.category === 'Agressividade'
        ? 'C-bet no flop e turn'
        : 'Pre-flop: decisao de raise ou fold',
    reason: `${leak.name} - impacto estimado ${Math.abs(leak.cost_bb).toFixed(1)} BB`,
    href: '/treinos',
  }));
}

function coerceArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function normalizeSession(raw: any, fallbackFilename?: string): SessionView {
  const id = raw?.id ?? raw?.session_id ?? `session-${Date.now()}`;
  const filename =
    raw?.filename ?? raw?.source_filename ?? fallbackFilename ?? `Sessao #${id}`;
  const handsCount =
    Number(
      raw?.hands_count ??
        raw?.hands_total ??
        raw?.stats?.ALL?.hands ??
        raw?.hands?.length ??
        0
    ) || 0;
  const evTotal = Number(raw?.ev_total ?? raw?.net_ev_bb ?? raw?.ev ?? 0) || 0;
  const winrate = Number(raw?.winrate ?? raw?.stats?.ALL?.vpipNetBb ?? raw?.win_rate ?? evTotal * 3.2) || 0;
  const roi = Number(raw?.roi ?? (handsCount > 0 ? (evTotal / Math.max(handsCount, 1)) * 100 : winrate)) || 0;
  const score =
    Number(raw?.score ?? raw?.gto_score ?? clamp(65 + winrate * 1.8 + evTotal * 2.5, 32, 96)) || 0;
  const stackData = coerceArray<StackPoint>(raw?.stack_data).length
    ? coerceArray<StackPoint>(raw?.stack_data)
    : buildStackData(handsCount || 24, evTotal || 1.2);
  const baseSession = {
    id,
    created_at: raw?.created_at ?? raw?.date ?? raw?.meta?.date ?? new Date().toISOString(),
    filename,
    hands_count: handsCount,
    winrate,
    ev_total: evTotal,
    roi,
    score: Math.round(score),
    stack_data: stackData,
    leaks: [] as LeakItem[],
    hands: [] as HandItem[],
    drills: [] as DrillItem[],
    coachSummary:
      raw?.coach?.summary ?? raw?.coach?.priority_action ?? raw?.summary ?? undefined,
    isMock: Boolean(raw?.isMock),
  };

  const leaks = coerceArray<LeakItem>(raw?.leaks);
  const hands = coerceArray<HandItem>(raw?.hands);
  const resolvedLeaks = leaks.length ? leaks : fallbackLeaks(baseSession);
  const resolvedHands = hands.length ? hands : fallbackHands(baseSession);
  const drills = coerceArray<DrillItem>(raw?.drills);

  return {
    ...baseSession,
    leaks: resolvedLeaks,
    hands: resolvedHands,
    drills: drills.length ? drills : fallbackDrills(resolvedLeaks),
  };
}

function metricComparison(
  current: number,
  previous: number | undefined,
  fallbackLabel: string
) {
  if (typeof previous !== 'number' || Number.isNaN(previous)) {
    return fallbackLabel;
  }
  const diff = current - previous;
  const prefix = diff > 0 ? '+' : '';
  return `${prefix}${diff.toFixed(1)} vs sessao anterior`;
}

function qualityBadge(quality: HandItem['quality']) {
  if (quality === 'good') {
    return {
      label: 'Otimo!',
      color: COLORS.green,
      background: 'rgba(0,230,51,0.1)',
      border: 'rgba(0,230,51,0.2)',
    };
  }

  return {
    label: 'Revisar',
    color: COLORS.red,
    background: 'rgba(229,57,53,0.1)',
    border: 'rgba(229,57,53,0.2)',
  };
}

function buildTrainingPack(session: SessionView, focusDrill: string): TrainingPackState {
  return {
    trainingPack: {
      generatedAt: new Date().toISOString(),
      focusDrill,
      session: {
        id: session.id,
        filename: session.filename,
        created_at: session.created_at,
        hands_count: session.hands_count,
        winrate: session.winrate,
        ev_total: session.ev_total,
        roi: session.roi,
        score: session.score,
      },
      leaks: session.leaks,
      drills: session.drills,
    },
  };
}

function normalizeComparisonKey(value: string) {
  return value.trim().toLowerCase();
}

function resolveLeakFocusId(leak: Pick<LeakItem, 'name' | 'category'>) {
  const name = leak.name.toLowerCase();
  const category = leak.category.toLowerCase();

  if (name.includes('btn') || category.includes('posi')) return 'btn-fold';
  if (name.includes('c-bet') || category.includes('agress')) return 'cbet-flop';
  if (name.includes('sb') || category.includes('pre-flop')) return 'sb-preflop';

  return normalizeComparisonKey(`${leak.category}-${leak.name}`);
}

function compareLeaks(
  currentLeaks: LeakItem[],
  previousLeaks: LeakItem[] | undefined
): LeakTrendItem[] {
  const previousMap = new Map(
    (previousLeaks ?? []).map((leak) => [normalizeComparisonKey(`${leak.category}-${leak.name}`), leak])
  );

  return currentLeaks.slice(0, 4).map((leak) => {
    const key = normalizeComparisonKey(`${leak.category}-${leak.name}`);
    const previous = previousMap.get(key);

    if (!previous) {
      return {
        name: leak.name,
        category: leak.category,
        currentCost: leak.cost_bb,
        currentSeverity: leak.severity,
        trend: 'new',
        message: 'Novo leak monitorado nesta sessao',
      };
    }

    const currentImpact = Math.abs(leak.cost_bb);
    const previousImpact = Math.abs(previous.cost_bb);
    const impactDelta = Number((previousImpact - currentImpact).toFixed(1));
    const severityDelta = Number((previous.severity - leak.severity).toFixed(2));

    if (Math.abs(impactDelta) < 0.15 && Math.abs(severityDelta) < 0.04) {
      return {
        name: leak.name,
        category: leak.category,
        currentCost: leak.cost_bb,
        previousCost: previous.cost_bb,
        currentSeverity: leak.severity,
        previousSeverity: previous.severity,
        trend: 'stable',
        message: 'Praticamente estavel vs sessao anterior',
      };
    }

    if (impactDelta > 0 || severityDelta > 0) {
      return {
        name: leak.name,
        category: leak.category,
        currentCost: leak.cost_bb,
        previousCost: previous.cost_bb,
        currentSeverity: leak.severity,
        previousSeverity: previous.severity,
        trend: 'improved',
        message: `${impactDelta > 0 ? `-${impactDelta.toFixed(1)} BB de custo` : 'Severidade menor'} vs anterior`,
      };
    }

    return {
      name: leak.name,
      category: leak.category,
      currentCost: leak.cost_bb,
      previousCost: previous.cost_bb,
      currentSeverity: leak.severity,
      previousSeverity: previous.severity,
      trend: 'worse',
      message: `${Math.abs(impactDelta).toFixed(1)} BB a mais de custo estimado`,
    };
  });
}

function buildLeakWindowInsights(
  selected: SessionView | null,
  allSessions: SessionView[],
  windowSize = 5
): LeakWindowInsight[] {
  if (!selected) return [];

  const selectedIndex = allSessions.findIndex(
    (session) => String(session.id) === String(selected.id)
  );
  const windowSessions =
    selectedIndex >= 0 ? allSessions.slice(selectedIndex, selectedIndex + windowSize) : [selected];
  const orderedWindowSessions = [...windowSessions].reverse();

  return selected.leaks.slice(0, 4).map((leak) => {
    const key = normalizeComparisonKey(`${leak.category}-${leak.name}`);
    const matches = windowSessions
      .map((session) =>
        session.leaks.find(
          (candidate) =>
            normalizeComparisonKey(`${candidate.category}-${candidate.name}`) === key
        )
      )
      .filter(Boolean) as LeakItem[];

    const appearances = matches.length;
    const series = orderedWindowSessions.map((session, index) => {
      const matchedLeak = session.leaks.find(
        (candidate) =>
          normalizeComparisonKey(`${candidate.category}-${candidate.name}`) === key
      );

      return {
        slot: `S${index + 1}`,
        cost: matchedLeak ? Math.abs(matchedLeak.cost_bb) : 0,
      };
    });
    const averageCost = appearances
      ? Number(
          (
            matches.reduce((sum, item) => sum + Math.abs(item.cost_bb), 0) / appearances
          ).toFixed(1)
        )
      : 0;

    if (appearances <= 1) {
      return {
        name: leak.name,
        category: leak.category,
        appearances,
        sessionsConsidered: windowSessions.length,
        averageCost,
        trend: 'new',
        message: 'Leak isolado na janela recente',
        series,
      };
    }

    const oldest = Math.abs(matches[matches.length - 1].cost_bb);
    const newest = Math.abs(matches[0].cost_bb);
    const delta = Number((oldest - newest).toFixed(1));

    if (Math.abs(delta) < 0.15) {
      return {
        name: leak.name,
        category: leak.category,
        appearances,
        sessionsConsidered: windowSessions.length,
        averageCost,
        trend: 'stable',
        message: `Recorrente em ${appearances}/${windowSessions.length} sessoes, impacto estavel`,
        series,
      };
    }

    if (delta > 0) {
      return {
        name: leak.name,
        category: leak.category,
        appearances,
        sessionsConsidered: windowSessions.length,
        averageCost,
        trend: 'improved',
        message: `Melhorando na janela curta, -${delta.toFixed(1)} BB vs leitura mais antiga`,
        series,
      };
    }

    return {
      name: leak.name,
      category: leak.category,
      appearances,
      sessionsConsidered: windowSessions.length,
      averageCost,
      trend: 'worse',
      message: `Piorando na janela curta, +${Math.abs(delta).toFixed(1)} BB de impacto`,
      series,
    };
  });
}

function SessionMetric({
  label,
  value,
  positive,
  comparison,
}: {
  label: string;
  value: string;
  positive: boolean;
  comparison: string;
}) {
  return (
    <div className="pm-sessions-metric-card">
      <div className="pm-sessions-metric-label">{label}</div>
      <div
        className={positive ? 'pm-sessions-metric-value positive' : 'pm-sessions-metric-value negative'}
      >
        {value}
      </div>
      <div className="pm-sessions-metric-meta">{comparison}</div>
    </div>
  );
}

function SessionHandsModal({
  hand,
  onClose,
}: {
  hand: HandItem | null;
  onClose: () => void;
}) {
  if (!hand) return null;

  const boardCards = parseBoardCards(hand.board);

  return (
    <div className="pm-sessions-modal-backdrop" onClick={onClose}>
      <div className="pm-sessions-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pm-sessions-modal-header">
          <div>
            <div className="pm-sessions-modal-title">Detalhe da Mao</div>
            <div className="pm-sessions-modal-subtitle">{hand.position}</div>
          </div>
          <button className="pm-sessions-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="pm-sessions-modal-block">
          <div className="pm-sessions-modal-label">Hero</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <PlayingCard value={hand.value} suit={hand.suit} size="md" />
            <PlayingCard value={hand.value2} suit={hand.suit2} size="md" />
          </div>
        </div>

        <div className="pm-sessions-modal-block">
          <div className="pm-sessions-modal-label">Board</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {boardCards.length > 0
              ? boardCards.map((card, index) => (
                  <PlayingCard key={`${card}-${index}`} value={card} size="sm" />
                ))
              : Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="pm-sessions-board-placeholder">
                    ?
                  </div>
                ))}
          </div>
          <div className="pm-sessions-board-text">{hand.board}</div>
        </div>

        <div className="pm-sessions-modal-block">
          <div className="pm-sessions-modal-label">Coach IA</div>
          <div className="pm-sessions-modal-analysis">
            {hand.analysis ??
              'Spot separado para revisao detalhada. Vale investigar ranges, sizings e o impacto do runout na sua linha.'}
          </div>
        </div>

        <div className="pm-sessions-modal-footer">
          <div
            style={{
              color: hand.ev >= 0 ? COLORS.green : COLORS.red,
              fontWeight: 800,
              fontSize: 16,
            }}
          >
            {formatMoneyEv(hand.ev)}
          </div>
          <Link
            to="/coach"
            state={{
              preloadedHand: {
                cards: [`${hand.value}${hand.suit}`, `${hand.value2}${hand.suit2}`],
                board: boardCards,
                position: hand.position,
                ev: hand.ev,
                boardText: hand.board,
                analysis: hand.analysis,
              },
              autoSend: true,
            }}
            className="pm-sessions-coach-link"
          >
            Analisar no Coach →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Sessions() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const leaksSectionRef = useRef<HTMLDivElement | null>(null);
  const [sessions, setSessions] = useState<SessionView[]>([]);
  const [selectedId, setSelectedId] = useState<SessionId | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [filter, setFilter] = useState<HandFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('default');
  const [dragActive, setDragActive] = useState(false);
  const [uploadExpanded, setUploadExpanded] = useState(true);
  const [selectedHand, setSelectedHand] = useState<HandItem | null>(null);
  const [overview, setOverview] = useState<StatsOverview | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showMockPreview, setShowMockPreview] = useState(false);
  const [hydratedDetails, setHydratedDetails] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const focusLeakId =
    (location.state as SessionsLocationState | null)?.focusLeakId ?? null;

  const hasRealSessions = sessions.length > 0;
  const displaySessions = hasRealSessions ? sessions : [MOCK_SESSION];
  const selected =
    displaySessions.find((session) => String(session.id) === String(selectedId)) ??
    displaySessions[0] ??
    null;
  const usingMockFallback = !hasRealSessions;

  const filteredHands = useMemo(() => {
    const hands = selected?.hands ?? [];
    const next = hands.filter((hand) => {
      if (filter === 'positive') return hand.ev > 0;
      if (filter === 'negative') return hand.ev < 0;
      return true;
    });

    if (sortMode === 'position') {
      return [...next].sort((a, b) => a.position.localeCompare(b.position, 'pt-BR'));
    }

    return next;
  }, [filter, selected?.hands, sortMode]);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const [sessionsResponse, overviewResponse] = await Promise.all([
        apiFetch('/sessions'),
        apiFetch('/stats/overview').catch(() => null),
      ]);

      const rawSessions = Array.isArray(sessionsResponse)
        ? sessionsResponse
        : Array.isArray(sessionsResponse?.sessions)
        ? sessionsResponse.sessions
        : [];
      const normalized = rawSessions.map((item: any) => normalizeSession(item));

      setSessions(normalized);
      setOverview(overviewResponse as StatsOverview | null);
      queryClient.setQueryData(queryKeys.sessions, { sessions: rawSessions });
      if (overviewResponse) {
        queryClient.setQueryData(queryKeys.stats, overviewResponse);
      }
      setSelectedId(normalized[0]?.id ?? MOCK_SESSION.id);
      setShowMockPreview(normalized.length === 0);
    } catch (error) {
      setSessions([]);
      setSelectedId(MOCK_SESSION.id);
      setShowMockPreview(true);
      setErrorMessage(
        error instanceof Error
          ? `${error.message}. Exibindo uma sessao demo enquanto a API nao responde.`
          : 'Erro ao carregar sessoes. Exibindo uma sessao demo.'
      );
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (!selected || selected.isMock || hydratedDetails[String(selected.id)]) {
      return;
    }

    let active = true;

    async function hydrateSelectedSession() {
      try {
        const response = await apiFetch(`/sessions/${selected.id}`);
        if (!active) return;
        const normalized = normalizeSession(response, selected.filename);
        setSessions((prev) =>
          prev.map((session) =>
            String(session.id) === String(selected.id) ? { ...session, ...normalized } : session
          )
        );
        setHydratedDetails((prev) => ({ ...prev, [String(selected.id)]: true }));
      } catch {
        if (!active) return;
        setHydratedDetails((prev) => ({ ...prev, [String(selected.id)]: true }));
      }
    }

    void hydrateSelectedSession();

    return () => {
      active = false;
    };
  }, [hydratedDetails, selected]);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const processFile = useCallback(
    async (file: File | null) => {
      if (!file || !file.name.toLowerCase().endsWith('.txt')) {
        setUploadState('error');
        return;
      }

      setUploading(true);
      setUploadState('uploading');
      setErrorMessage(null);

      try {
        await file.text();

        const formData = new FormData();
        formData.append('file', file);
        formData.append('source', 'ggpoker');

        const analyzeResponse = await apiFetch('/analyze', {
          method: 'POST',
          body: formData,
        });

        const possibleId =
          analyzeResponse?.session?.id ??
          analyzeResponse?.session_id ??
          analyzeResponse?.id ??
          null;

        let createdSession = normalizeSession(analyzeResponse, file.name);

        if (possibleId != null) {
          try {
            const sessionResponse = await apiFetch(`/sessions/${possibleId}`);
            createdSession = normalizeSession(sessionResponse, file.name);
          } catch {
            createdSession = {
              ...createdSession,
              id: possibleId,
              filename: file.name,
            };
          }
        }

        setSessions((prev) => [createdSession, ...prev.filter((item) => String(item.id) !== String(createdSession.id))]);
        setSelectedId(createdSession.id);
        setShowMockPreview(false);
        setUploadState('success');
        setUploadExpanded(false);
        setOverview((prev) =>
          prev
            ? {
                ...prev,
                sessions_count: (prev.sessions_count ?? 0) + 1,
              }
            : prev
        );

        window.setTimeout(() => {
          setUploadState('idle');
        }, 1200);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
          queryClient.invalidateQueries({ queryKey: queryKeys.stats }),
        ]);
      } catch (error) {
        setUploadState('error');
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Erro ao processar. Verifique se e um arquivo .txt valido do GGPoker.'
        );
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [queryClient]
  );

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      await processFile(file);
    },
    [processFile]
  );

  const handleDelete = useCallback(
    async (id: SessionId) => {
      const target = sessions.find((session) => String(session.id) === String(id));
      if (!target || target.isMock) return;

      const confirmed = window.confirm('Deseja deletar esta sessao analisada?');
      if (!confirmed) return;

      try {
        await apiFetch(`/sessions/${id}`, { method: 'DELETE' });
        const nextSessions = sessions.filter((session) => String(session.id) !== String(id));
        setSessions(nextSessions);
        setSelectedId(nextSessions[0]?.id ?? MOCK_SESSION.id);
        setShowMockPreview(nextSessions.length === 0);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
          queryClient.invalidateQueries({ queryKey: queryKeys.stats }),
        ]);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Nao foi possivel deletar a sessao.'
        );
      }
    },
    [queryClient, sessions]
  );

  const previousSession = useMemo(() => {
    if (!selected || usingMockFallback) return null;
    const index = sessions.findIndex((session) => String(session.id) === String(selected.id));
    return index >= 0 ? sessions[index + 1] ?? null : null;
  }, [selected, sessions, usingMockFallback]);

  const leakTrends = useMemo(
    () => compareLeaks(selected?.leaks ?? [], previousSession?.leaks),
    [previousSession?.leaks, selected?.leaks]
  );

  const comparisonSummary = useMemo(() => {
    return {
      improved: leakTrends.filter((item) => item.trend === 'improved').length,
      worse: leakTrends.filter((item) => item.trend === 'worse').length,
      emerging: leakTrends.filter((item) => item.trend === 'new').length,
    };
  }, [leakTrends]);

  const leakWindowInsights = useMemo(
    () => buildLeakWindowInsights(selected, sessions),
    [selected, sessions]
  );

  const leakWindowSummary = useMemo(() => {
    return {
      recurring: leakWindowInsights.filter((item) => item.appearances >= 2).length,
      improving: leakWindowInsights.filter((item) => item.trend === 'improved').length,
      averageImpact: leakWindowInsights.length
        ? Number(
            (
              leakWindowInsights.reduce((sum, item) => sum + item.averageCost, 0) /
              leakWindowInsights.length
            ).toFixed(1)
          )
        : 0,
    };
  }, [leakWindowInsights]);

  const hasFocusedLeakMatch = useMemo(
    () =>
      Boolean(
        focusLeakId &&
          selected?.leaks.some((leak) => resolveLeakFocusId(leak) === focusLeakId)
      ),
    [focusLeakId, selected?.leaks]
  );

  useEffect(() => {
    if (!hasFocusedLeakMatch || !leaksSectionRef.current) return;

    leaksSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hasFocusedLeakMatch]);

  return (
    <PageLayout activeRoute="/sessions">
      <style>{`
        @keyframes pmSessionsPulse {
          0% { opacity: 0.45; transform: translateX(-25%); }
          50% { opacity: 1; transform: translateX(0); }
          100% { opacity: 0.45; transform: translateX(25%); }
        }
        @keyframes pmSessionsAppear {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pm-sessions-root {
          min-height: calc(100vh - 64px);
          color: ${COLORS.white};
          font-family: ${FONT};
          background: ${COLORS.bg};
        }
        .pm-sessions-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 28px;
          flex-wrap: wrap;
        }
        .pm-sessions-title {
          margin: 0;
          font-size: 24px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: -0.5px;
          color: ${COLORS.white};
          font-family: ${FONT};
        }
        .pm-sessions-subtitle {
          margin-top: 4px;
          font-size: 13px;
          color: ${COLORS.muted};
        }
        .pm-sessions-primary-btn {
          background: linear-gradient(135deg, ${COLORS.green}, ${COLORS.blue});
          color: #000;
          border: none;
          border-radius: 8px;
          padding: 10px 20px;
          font-size: 13px;
          font-weight: 800;
          font-family: ${FONT};
          cursor: pointer;
        }
        .pm-sessions-upload-link {
          background: transparent;
          border: none;
          padding: 0;
          color: rgba(255,255,255,0.62);
          cursor: pointer;
          font-size: 13px;
          font-weight: 700;
          font-family: ${FONT};
        }
        .pm-sessions-dropzone {
          background: rgba(0,230,51,0.02);
          border: 2px dashed rgba(0,230,51,0.15);
          border-radius: 12px;
          padding: 28px;
          text-align: center;
          margin-bottom: 24px;
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s, transform 0.15s;
        }
        .pm-sessions-dropzone.drag,
        .pm-sessions-dropzone:hover {
          border-color: rgba(0,230,51,0.4);
          background: rgba(0,230,51,0.04);
          transform: translateY(-1px);
        }
        .pm-sessions-dropzone.error {
          border-color: rgba(229,57,53,0.45);
          background: rgba(229,57,53,0.04);
        }
        .pm-sessions-upload-status {
          margin-top: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: rgba(255,255,255,0.72);
          font-size: 13px;
          font-weight: 700;
        }
        .pm-sessions-upload-bar {
          width: min(280px, 100%);
          height: 6px;
          border-radius: 999px;
          background: rgba(255,255,255,0.08);
          margin: 12px auto 0;
          overflow: hidden;
        }
        .pm-sessions-upload-bar > span {
          display: block;
          width: 55%;
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, ${COLORS.green}, ${COLORS.lime});
          animation: pmSessionsPulse 1.2s ease-in-out infinite;
        }
        .pm-sessions-empty {
          min-height: calc(100vh - 180px);
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          flex-direction: column;
          animation: pmSessionsAppear 0.25s ease-out;
        }
        .pm-sessions-layout {
          display: grid;
          grid-template-columns: 320px minmax(0, 1fr);
          gap: 24px;
          align-items: flex-start;
        }
        .pm-sessions-list {
          max-height: calc(100vh - 180px);
          overflow-y: auto;
          padding-right: 16px;
        }
        .pm-session-card {
          position: relative;
          background: ${COLORS.card};
          border: 1px solid ${COLORS.border};
          border-radius: 10px;
          padding: 14px 16px;
          margin-bottom: 8px;
          cursor: pointer;
          transition: all 0.15s;
        }
        .pm-session-card:hover {
          border-color: rgba(0,230,51,0.2);
          background: rgba(0,230,51,0.03);
        }
        .pm-session-card.active {
          border-color: rgba(0,230,51,0.4);
          background: rgba(0,230,51,0.05);
          box-shadow: inset 3px 0 0 ${COLORS.green};
        }
        .pm-session-delete {
          position: absolute;
          top: 10px;
          right: 10px;
          color: rgba(255,255,255,0.2);
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 20px;
          line-height: 1;
        }
        .pm-session-delete:hover {
          color: ${COLORS.red};
        }
        .pm-session-score {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-weight: 900;
          font-family: ${FONT};
        }
        .pm-sessions-detail-section,
        .pm-sessions-metric-card {
          background: ${COLORS.card};
          border: 1px solid ${COLORS.border};
          border-radius: 10px;
        }
        .pm-sessions-comparison-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 18px;
        }
        .pm-sessions-summary-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }
        .pm-sessions-metric-card {
          padding: 16px;
        }
        .pm-sessions-metric-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.3);
          margin-bottom: 6px;
        }
        .pm-sessions-metric-value {
          font-size: 28px;
          font-weight: 900;
          font-family: ${FONT};
          line-height: 1.05;
        }
        .pm-sessions-metric-value.positive {
          background: linear-gradient(135deg, ${COLORS.green}, ${COLORS.lime});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .pm-sessions-metric-value.negative {
          color: ${COLORS.red};
        }
        .pm-sessions-metric-meta {
          font-size: 11px;
          color: rgba(255,255,255,0.25);
          margin-top: 8px;
        }
        .pm-sessions-detail-section {
          padding: 20px;
          margin-bottom: 20px;
        }
        .pm-sessions-comparison-card {
          background: ${COLORS.cardAlt};
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 10px;
          padding: 14px;
        }
        .pm-sessions-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          color: ${COLORS.white};
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 16px;
        }
        .pm-sessions-leak-row,
        .pm-sessions-hand-row,
        .pm-sessions-drill-row {
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .pm-sessions-leak-row.focused {
          background: rgba(0,155,255,0.08);
          border: 1px solid rgba(0,155,255,0.18);
          border-radius: 10px;
          padding: 0 12px;
          margin-bottom: 8px;
        }
        .pm-sessions-leak-row:last-child,
        .pm-sessions-hand-row:last-child,
        .pm-sessions-drill-row:last-child {
          border-bottom: none;
        }
        .pm-sessions-pill-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }
        .pm-sessions-pill {
          background: ${COLORS.cardAlt};
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px;
          padding: 4px 12px;
          font-size: 11px;
          font-family: ${FONT};
          color: rgba(255,255,255,0.55);
          cursor: pointer;
        }
        .pm-sessions-pill.active {
          border-color: ${COLORS.green};
          color: ${COLORS.green};
        }
        .pm-sessions-trend-row {
          padding: 12px 0;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .pm-sessions-trend-row:last-child {
          border-bottom: none;
        }
        .pm-sessions-hand-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 0;
          cursor: pointer;
          border-radius: 6px;
          transition: background 0.15s;
        }
        .pm-sessions-hand-row:hover {
          background: rgba(255,255,255,0.02);
        }
        .pm-sessions-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.72);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          z-index: 120;
        }
        .pm-sessions-modal {
          width: min(640px, 100%);
          background: ${COLORS.card};
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 24px;
          font-family: ${FONT};
        }
        .pm-sessions-modal-header,
        .pm-sessions-modal-footer,
        .pm-sessions-drill-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .pm-sessions-modal-title {
          font-size: 18px;
          font-weight: 900;
          color: ${COLORS.white};
        }
        .pm-sessions-modal-subtitle {
          margin-top: 4px;
          color: ${COLORS.muted};
          font-size: 13px;
        }
        .pm-sessions-modal-close {
          border: none;
          background: transparent;
          color: rgba(255,255,255,0.45);
          font-size: 28px;
          cursor: pointer;
          line-height: 1;
        }
        .pm-sessions-modal-block {
          margin-top: 20px;
        }
        .pm-sessions-modal-label {
          color: rgba(255,255,255,0.35);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          margin-bottom: 10px;
        }
        .pm-sessions-modal-analysis {
          background: ${COLORS.cardAlt};
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 10px;
          padding: 14px 16px;
          color: rgba(255,255,255,0.72);
          line-height: 1.7;
          font-size: 13px;
        }
        .pm-sessions-board-placeholder {
          width: 28px;
          height: 36px;
          border-radius: 4px;
          border: 1px solid rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.25);
          background: ${COLORS.cardAlt};
        }
        .pm-sessions-board-text {
          margin-top: 10px;
          color: rgba(255,255,255,0.35);
          font-size: 12px;
        }
        .pm-sessions-coach-link,
        .pm-sessions-drill-link {
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          font-weight: 700;
          font-family: ${FONT};
        }
        .pm-sessions-coach-link {
          background: linear-gradient(135deg, ${COLORS.green}, ${COLORS.blue});
          color: #000;
          padding: 10px 16px;
          font-size: 12px;
        }
        .pm-sessions-drills {
          background: ${COLORS.card};
          border: 1px solid rgba(0,155,255,0.12);
          border-radius: 10px;
          padding: 20px;
        }
        .pm-sessions-drill-card {
          background: ${COLORS.cardAlt};
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 8px;
          padding: 14px;
          margin-bottom: 8px;
        }
        .pm-sessions-drill-card:last-child {
          margin-bottom: 0;
        }
        .pm-sessions-drill-link {
          background: linear-gradient(135deg, ${COLORS.blue}, #0052CC);
          color: ${COLORS.white};
          padding: 6px 14px;
          font-size: 11px;
          flex-shrink: 0;
        }
        .pm-sessions-banner {
          background: rgba(0,155,255,0.08);
          border: 1px solid rgba(0,155,255,0.18);
          border-radius: 10px;
          padding: 12px 14px;
          margin-bottom: 16px;
          color: rgba(255,255,255,0.74);
          font-size: 13px;
        }
        @media (max-width: 1280px) {
          .pm-sessions-layout {
            grid-template-columns: 1fr;
          }
          .pm-sessions-list {
            max-height: none;
            padding-right: 0;
          }
          .pm-sessions-comparison-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 1040px) {
          .pm-sessions-summary-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 720px) {
          .pm-sessions-summary-grid {
            grid-template-columns: 1fr;
          }
          .pm-sessions-modal {
            padding: 18px;
          }
          .pm-sessions-drill-row,
          .pm-sessions-modal-header,
          .pm-sessions-modal-footer,
          .pm-sessions-header {
            align-items: flex-start;
            flex-direction: column;
          }
        }
      `}</style>

      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background:
            'linear-gradient(90deg, transparent, #00E633 30%, #009BFF 70%, transparent)',
          zIndex: 9999,
        }}
      />

      <div className="pm-sessions-root">
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt"
          hidden
          onChange={handleFileUpload}
        />

        <div className="pm-sessions-header">
          <div>
            <h1 className="pm-sessions-title">Analise de Maos</h1>
            <div className="pm-sessions-subtitle">
              Sessoes do GGPoker analisadas
              {overview?.sessions_count ? ` · ${overview.sessions_count} sessoes no total` : ''}
            </div>
          </div>
          <button className="pm-sessions-primary-btn" onClick={openFilePicker}>
            Carregar sessao +
          </button>
        </div>

        {errorMessage ? <div className="pm-sessions-banner">{errorMessage}</div> : null}

        {hasRealSessions ? (
          uploadExpanded ? (
            <div
              className={`pm-sessions-dropzone ${dragActive ? 'drag' : ''} ${uploadState === 'error' ? 'error' : ''}`}
              onClick={openFilePicker}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                const file = event.dataTransfer.files?.[0] ?? null;
                void processFile(file);
              }}
            >
              <div style={{ fontSize: 28, color: 'rgba(0,230,51,0.4)', marginBottom: 8 }}>↑</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
                Arraste o arquivo .txt do GGPoker aqui
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', marginTop: 4 }}>
                ou clique para selecionar
              </div>

              {uploadState === 'uploading' ? (
                <>
                  <div className="pm-sessions-upload-status">
                    <span style={{ color: COLORS.green }}>◌</span>
                    <span>Analisando sua sessao...</span>
                  </div>
                  <div className="pm-sessions-upload-bar">
                    <span />
                  </div>
                </>
              ) : null}

              {uploadState === 'success' ? (
                <div className="pm-sessions-upload-status" style={{ color: COLORS.green }}>
                  <span>✓</span>
                  <span>Sessao analisada!</span>
                </div>
              ) : null}

              {uploadState === 'error' ? (
                <div className="pm-sessions-upload-status" style={{ color: COLORS.red }}>
                  Erro ao processar. Verifique se e um arquivo .txt valido do GGPoker.
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ marginBottom: 24 }}>
              <button
                className="pm-sessions-upload-link"
                onClick={() => setUploadExpanded(true)}
              >
                Carregar nova sessao ↑
              </button>
            </div>
          )
        ) : null}

        {loading ? (
          <div
            style={{
              minHeight: '50vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.muted,
              fontSize: 14,
            }}
          >
            Carregando sessoes analisadas...
          </div>
        ) : null}

        {!loading && usingMockFallback ? (
          <>
            <div className="pm-sessions-empty">
              <div
                style={{
                  fontSize: 64,
                  color: 'rgba(0,230,51,0.2)',
                  marginBottom: 16,
                }}
              >
                ♠
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                Nenhuma sessao analisada ainda
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: 'rgba(255,255,255,0.35)',
                  maxWidth: 360,
                  textAlign: 'center',
                  marginTop: 10,
                  lineHeight: 1.7,
                }}
              >
                Faca upload do seu historico .txt do GGPoker e receba analise completa em segundos.
              </div>
              <button
                className="pm-sessions-primary-btn"
                style={{ padding: '12px 28px', marginTop: 20 }}
                onClick={openFilePicker}
              >
                Carregar minha primeira sessao
              </button>
            </div>

            {showMockPreview ? (
              <div style={{ marginTop: 12 }}>
                <div className="pm-sessions-banner">
                  Exibindo uma sessao demo para voce visualizar a experiencia completa enquanto ainda nao ha sessoes reais na conta.
                </div>
                <div
                  className={`pm-sessions-dropzone ${dragActive ? 'drag' : ''} ${uploadState === 'error' ? 'error' : ''}`}
                  onClick={openFilePicker}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    setDragActive(false);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragActive(false);
                    const file = event.dataTransfer.files?.[0] ?? null;
                    void processFile(file);
                  }}
                >
                  <div style={{ fontSize: 28, color: 'rgba(0,230,51,0.4)', marginBottom: 8 }}>↑</div>
                  <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
                    Arraste o arquivo .txt do GGPoker aqui
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', marginTop: 4 }}>
                    ou clique para selecionar
                  </div>
                  {uploadState === 'uploading' ? (
                    <>
                      <div className="pm-sessions-upload-status">
                        <span style={{ color: COLORS.green }}>◌</span>
                        <span>Analisando sua sessao...</span>
                      </div>
                      <div className="pm-sessions-upload-bar">
                        <span />
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        {!loading && selected ? (
          <div className="pm-sessions-layout">
            <div className="pm-sessions-list">
              {displaySessions.map((session) => {
                const isActive = String(session.id) === String(selected.id);
                const evPositive = session.ev_total >= 0;
                return (
                  <div
                    key={String(session.id)}
                    className={`pm-session-card ${isActive ? 'active' : ''}`}
                    onClick={() => setSelectedId(session.id)}
                  >
                    {!session.isMock ? (
                      <button
                        className="pm-session-delete"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleDelete(session.id);
                        }}
                      >
                        ×
                      </button>
                    ) : null}

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 12,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
                          {formatDate(session.created_at)}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: COLORS.white,
                            marginTop: 6,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {session.filename || `Sessao #${session.id}`}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: 12,
                            flexWrap: 'wrap',
                            marginTop: 8,
                            fontSize: 11,
                            color: 'rgba(255,255,255,0.4)',
                          }}
                        >
                          <span>Maos: {session.hands_count}</span>
                          <span>EV: {formatSigned(session.ev_total, 'BB')}</span>
                          <span>ROI: {formatSigned(session.roi, '%')}</span>
                        </div>
                      </div>
                      <div
                        className="pm-session-score"
                        style={{ color: evPositive ? COLORS.green : COLORS.red }}
                      >
                        {session.score}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div>
              <div className="pm-sessions-summary-grid">
                <SessionMetric
                  label="Winrate"
                  value={`${formatSigned(selected.winrate, ' BB/100')}`}
                  positive={selected.winrate >= 0}
                  comparison={metricComparison(
                    selected.winrate,
                    previousSession?.winrate ?? overview?.win_rate,
                    'Sem comparativo anterior'
                  )}
                />
                <SessionMetric
                  label="EV Total"
                  value={`${formatSigned(selected.ev_total, ' BB')}`}
                  positive={selected.ev_total >= 0}
                  comparison={metricComparison(
                    selected.ev_total,
                    previousSession?.ev_total,
                    'Primeira sessao selecionada'
                  )}
                />
                <SessionMetric
                  label="ROI"
                  value={`${formatSigned(selected.roi, '%')}`}
                  positive={selected.roi >= 0}
                  comparison={metricComparison(
                    selected.roi,
                    previousSession?.roi,
                    'Referencia da sessao atual'
                  )}
                />
                <SessionMetric
                  label="Maos"
                  value={String(selected.hands_count)}
                  positive={selected.hands_count >= 0}
                  comparison={metricComparison(
                    selected.hands_count,
                    previousSession?.hands_count ?? overview?.avg_hands,
                    'Base para evolucao futura'
                  )}
                />
              </div>

              <div className="pm-sessions-detail-section">
                <div className="pm-sessions-section-title">
                  <span style={{ color: COLORS.blue }}>↔</span>
                  <span>Evolucao Entre Sessoes</span>
                </div>

                <div className="pm-sessions-comparison-grid">
                  {[
                    {
                      label: 'EV DELTA',
                      value:
                        previousSession != null
                          ? formatSigned(selected.ev_total - previousSession.ev_total, ' BB')
                          : '—',
                      tone:
                        previousSession == null
                          ? 'rgba(255,255,255,0.7)'
                          : selected.ev_total - previousSession.ev_total >= 0
                          ? COLORS.green
                          : COLORS.red,
                      meta: previousSession ? 'Comparado com a sessao anterior' : 'Sem base anterior',
                    },
                    {
                      label: 'LEAKS MELHORANDO',
                      value: String(comparisonSummary.improved),
                      tone: COLORS.green,
                      meta: previousSession
                        ? `${comparisonSummary.worse} piorando · ${comparisonSummary.emerging} novos`
                        : 'Disponivel quando houver historico',
                    },
                    {
                      label: 'SCORE GTO',
                      value:
                        previousSession != null
                          ? formatSigned(selected.score - previousSession.score)
                          : String(selected.score),
                      tone:
                        previousSession == null
                          ? 'rgba(255,255,255,0.7)'
                          : selected.score - previousSession.score >= 0
                          ? COLORS.green
                          : COLORS.red,
                      meta: previousSession
                        ? `Atual ${selected.score} vs anterior ${previousSession.score}`
                        : 'Primeira referencia carregada',
                    },
                  ].map((item) => (
                    <div key={item.label} className="pm-sessions-comparison-card">
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 2,
                          textTransform: 'uppercase',
                          color: 'rgba(255,255,255,0.3)',
                        }}
                      >
                        {item.label}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 24,
                          fontWeight: 900,
                          color: item.tone,
                        }}
                      >
                        {item.value}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          color: 'rgba(255,255,255,0.35)',
                          lineHeight: 1.6,
                        }}
                      >
                        {item.meta}
                      </div>
                    </div>
                  ))}
                </div>

                {previousSession ? (
                  leakTrends.map((item) => {
                    const badge =
                      item.trend === 'improved'
                        ? {
                            label: 'Melhorando',
                            color: COLORS.green,
                            background: 'rgba(0,230,51,0.1)',
                          }
                        : item.trend === 'worse'
                        ? {
                            label: 'Piorando',
                            color: COLORS.red,
                            background: 'rgba(229,57,53,0.1)',
                          }
                        : item.trend === 'new'
                        ? {
                            label: 'Novo',
                            color: COLORS.blue,
                            background: 'rgba(0,155,255,0.12)',
                          }
                        : {
                            label: 'Estavel',
                            color: 'rgba(255,255,255,0.7)',
                            background: 'rgba(255,255,255,0.08)',
                          };

                    return (
                      <div key={`${selected.id}-${item.name}-trend`} className="pm-sessions-trend-row">
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 16,
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: 'rgba(255,255,255,0.84)',
                                }}
                              >
                                {item.name}
                              </div>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  borderRadius: 999,
                                  padding: '4px 10px',
                                  fontSize: 10,
                                  fontWeight: 800,
                                  background: badge.background,
                                  color: badge.color,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.8,
                                }}
                              >
                                {badge.label}
                              </span>
                            </div>
                            <div
                              style={{
                                marginTop: 6,
                                color: 'rgba(255,255,255,0.4)',
                                fontSize: 12,
                              }}
                            >
                              {item.category} · {item.message}
                            </div>
                          </div>
                          <div
                            style={{
                              textAlign: 'right',
                              whiteSpace: 'nowrap',
                              color: item.trend === 'improved' ? COLORS.green : item.trend === 'worse' ? COLORS.red : 'rgba(255,255,255,0.7)',
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            {item.previousCost != null
                              ? `${formatSigned(item.previousCost, ' BB')} → ${formatSigned(item.currentCost, ' BB')}`
                              : formatSigned(item.currentCost, ' BB')}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ color: COLORS.muted, fontSize: 13 }}>
                    Assim que voce tiver pelo menos duas sessoes reais, esta area passa a mostrar quais leaks estao melhorando ou piorando.
                  </div>
                )}

                {sessions.length >= 2 ? (
                  <div
                    style={{
                      marginTop: 18,
                      paddingTop: 18,
                      borderTop: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 16,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        marginBottom: 12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 800,
                            color: 'rgba(255,255,255,0.84)',
                          }}
                        >
                          Tendencia nas ultimas {Math.min(5, sessions.length)} sessoes
                        </div>
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 12,
                            color: 'rgba(255,255,255,0.35)',
                          }}
                        >
                          Recorrencia e impacto medio dos leaks desta sessao dentro da janela recente
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span className="pm-sessions-pill">
                          recorrentes {leakWindowSummary.recurring}
                        </span>
                        <span className="pm-sessions-pill">
                          melhorando {leakWindowSummary.improving}
                        </span>
                        <span className="pm-sessions-pill">
                          impacto medio {leakWindowSummary.averageImpact} BB
                        </span>
                      </div>
                    </div>

                    {leakWindowInsights.map((item) => {
                      const badgeColor =
                        item.trend === 'improved'
                          ? COLORS.green
                          : item.trend === 'worse'
                          ? COLORS.red
                          : item.trend === 'new'
                          ? COLORS.blue
                          : 'rgba(255,255,255,0.72)';

                      return (
                        <div
                          key={`${selected?.id}-${item.name}-window`}
                          className="pm-sessions-trend-row"
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 16,
                              alignItems: 'flex-start',
                              flexWrap: 'wrap',
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: 'rgba(255,255,255,0.84)',
                                }}
                              >
                                {item.name}
                              </div>
                              <div
                                style={{
                                  width: '100%',
                                  maxWidth: 180,
                                  height: 34,
                                  marginTop: 10,
                                }}
                              >
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={item.series}>
                                    <Line
                                      type="monotone"
                                      dataKey="cost"
                                      stroke={badgeColor}
                                      strokeWidth={2}
                                      dot={false}
                                    />
                                  </LineChart>
                                </ResponsiveContainer>
                              </div>
                              <div
                                style={{
                                  marginTop: 6,
                                  fontSize: 12,
                                  color: 'rgba(255,255,255,0.38)',
                                  lineHeight: 1.6,
                                }}
                              >
                                {item.message}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 800,
                                  color: badgeColor,
                                }}
                              >
                                {item.appearances}/{item.sessionsConsidered} sessoes
                              </div>
                              <div
                                style={{
                                  marginTop: 6,
                                  fontSize: 11,
                                  color: 'rgba(255,255,255,0.35)',
                                }}
                              >
                                media {item.averageCost.toFixed(1)} BB
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div className="pm-sessions-detail-section">
                <div className="pm-sessions-section-title">Evolucao do Stack</div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={selected.stack_data}>
                    <CartesianGrid
                      stroke="rgba(255,255,255,0.04)"
                      strokeDasharray="3 3"
                    />
                    <XAxis
                      dataKey="mao"
                      tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
                      stroke="rgba(255,255,255,0.06)"
                    />
                    <YAxis
                      tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
                      stroke="rgba(255,255,255,0.06)"
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#1a1a1a',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontFamily: FONT,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="stack"
                      stroke={COLORS.green}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: COLORS.green }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="pm-sessions-detail-section" ref={leaksSectionRef}>
                <div className="pm-sessions-section-title">
                  <span style={{ color: COLORS.lime }}>⚠</span>
                  <span>Leaks Identificados</span>
                </div>
                {hasFocusedLeakMatch ? (
                  <div
                    style={{
                      marginBottom: 12,
                      background: 'rgba(0,155,255,0.08)',
                      border: '1px solid rgba(0,155,255,0.18)',
                      borderRadius: 10,
                      padding: '10px 12px',
                      color: 'rgba(255,255,255,0.76)',
                      fontSize: 12,
                    }}
                  >
                    Foco vindo do dashboard: o leak correspondente foi destacado nesta sessão.
                  </div>
                ) : null}
                {selected.leaks.map((leak) => (
                  <div
                    key={`${selected.id}-${leak.name}`}
                    className={`pm-sessions-leak-row ${
                      hasFocusedLeakMatch && resolveLeakFocusId(leak) === focusLeakId ? 'focused' : ''
                    }`}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 16,
                        padding: '10px 0',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontWeight: 700 }}>
                          {leak.name}
                        </div>
                        <div
                          style={{
                            display: 'inline-flex',
                            marginTop: 6,
                            fontSize: 9,
                            padding: '2px 8px',
                            background: 'rgba(255,255,255,0.06)',
                            borderRadius: 20,
                            color: 'rgba(255,255,255,0.4)',
                            textTransform: 'uppercase',
                            letterSpacing: 0.8,
                          }}
                        >
                          {leak.category}
                        </div>
                        <div
                          style={{
                            height: 3,
                            borderRadius: 2,
                            background: 'rgba(255,255,255,0.06)',
                            marginTop: 8,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${clamp(leak.severity * 100, 12, 100)}%`,
                              height: '100%',
                              borderRadius: 2,
                              background: 'linear-gradient(90deg, #e53935, #ff6b6b)',
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: COLORS.red,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatSigned(leak.cost_bb, ' BB')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pm-sessions-detail-section">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 16,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: 12,
                  }}
                >
                  <div className="pm-sessions-section-title" style={{ marginBottom: 0 }}>
                    Maos para Revisar
                  </div>
                  <div className="pm-sessions-pill-row" style={{ marginBottom: 0 }}>
                    {[
                      { id: 'all', label: 'Todas' },
                      { id: 'positive', label: 'Positivas' },
                      { id: 'negative', label: 'Negativas' },
                    ].map((item) => (
                      <button
                        key={item.id}
                        className={`pm-sessions-pill ${filter === item.id ? 'active' : ''}`}
                        onClick={() => setFilter(item.id as HandFilter)}
                      >
                        {item.label}
                      </button>
                    ))}
                    <button
                      className={`pm-sessions-pill ${sortMode === 'position' ? 'active' : ''}`}
                      onClick={() =>
                        setSortMode((prev) => (prev === 'position' ? 'default' : 'position'))
                      }
                    >
                      Por posicao ▼
                    </button>
                  </div>
                </div>

                {filteredHands.map((hand) => {
                  const badge = qualityBadge(hand.quality);
                  return (
                    <div
                      key={hand.id}
                      className="pm-sessions-hand-row"
                      onClick={() => setSelectedHand(hand)}
                    >
                      <div style={{ display: 'flex', gap: 4, marginRight: 12 }}>
                        <PlayingCard value={hand.value} suit={hand.suit} size="sm" />
                        <PlayingCard value={hand.value2} suit={hand.suit2} size="sm" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>
                          {hand.position}
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
                          {hand.board}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: hand.ev >= 0 ? COLORS.green : COLORS.red,
                          }}
                        >
                          {formatMoneyEv(hand.ev)}
                        </div>
                        <div
                          style={{
                            marginTop: 6,
                            display: 'inline-flex',
                            fontSize: 10,
                            padding: '3px 10px',
                            borderRadius: 20,
                            border: `1px solid ${badge.border}`,
                            background: badge.background,
                            color: badge.color,
                            fontWeight: 700,
                          }}
                        >
                          {badge.label}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {filteredHands.length === 0 ? (
                  <div style={{ color: COLORS.muted, fontSize: 13, paddingTop: 12 }}>
                    Nenhuma mao encontrada com o filtro atual.
                  </div>
                ) : null}
              </div>

              <div className="pm-sessions-drills">
                <div className="pm-sessions-section-title" style={{ marginBottom: 6 }}>
                  <span style={{ color: COLORS.blue }}>◎</span>
                  <span>Drills Recomendados</span>
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>
                  Baseado nos seus leaks desta sessao
                </div>
                {selected.drills.slice(0, 3).map((drill) => (
                  <div key={drill.name} className="pm-sessions-drill-card">
                    <div className="pm-sessions-drill-row">
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: 'rgba(255,255,255,0.8)',
                          }}
                        >
                          {drill.name}
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
                          {drill.reason}
                        </div>
                      </div>
                      <Link
                        to={drill.href}
                        state={buildTrainingPack(selected, drill.name)}
                        className="pm-sessions-drill-link"
                      >
                        Treinar →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <SessionHandsModal hand={selectedHand} onClose={() => setSelectedHand(null)} />
    </PageLayout>
  );
}
